import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router';
import { Header } from 'components/common/header/Header';
import { Button } from 'components/common/button/Button';
import { useMeetingDetail } from 'hooks/useMeeting';
import { meetingStore } from 'store/meetingStore';
import './MeetingEdit.css';

export const MeetingEdit = () => {
    const navigate = useNavigate();
    const { meetingId } = useParams();
    const { data: meetingData } = useMeetingDetail();

    // Pre-filled data from existing meeting
    const [meetingName, setMeetingName] = useState('');
    const [description, setDescription] = useState('');
    const [isRegularMeeting, setIsRegularMeeting] = useState(false);
    const [date, setDate] = useState('');
    const [time, setTime] = useState('');
    const [location, setLocation] = useState('');
    const [category, setCategory] = useState('');
    const [maxMembers, setMaxMembers] = useState(10);
    const [isPublic, setIsPublic] = useState(true);
    const [repeatType, setRepeatType] = useState<'WEEKLY' | 'MONTHLY'>('WEEKLY');
    const [repeatInterval, setRepeatInterval] = useState(1);

    // 여행 모임 관련 상태
    const [meetingType, setMeetingType] = useState<'DAY' | 'TRAVEL'>('DAY');
    const [travelDestination, setTravelDestination] = useState('');
    const [travelType, setTravelType] = useState<'DOMESTIC' | 'OVERSEAS'>('DOMESTIC');
    const [travelStartDate, setTravelStartDate] = useState<Date | null>(null);
    const [travelEndDate, setTravelEndDate] = useState<Date | null>(null);
    const [meetingTime, setMeetingTime] = useState('');
    const [hotelName, setHotelName] = useState('');
    const [hotelAddress, setHotelAddress] = useState('');

    // 컴포넌트 마운트 시 모임 상세 정보 조회
    useEffect(() => {
        if (meetingId) {
            meetingStore.onGetMeetingDetail(meetingId);
        }
    }, [meetingId]);

    // API 데이터로 폼 초기화
    useEffect(() => {
        if (meetingData?.data) {
            const meeting = meetingData.data;
            console.log('========== Meeting Edit Debug ==========');
            console.log('전체 API 데이터:', JSON.stringify(meeting, null, 2));
            console.log('confirmedScheduleId:', meeting.confirmedScheduleId);
            console.log('schedule 객체:', meeting.schedule);
            console.log('placeName:', meeting.placeName);
            console.log('placeAddress:', meeting.placeAddress);
            console.log('======================================');

            setMeetingName(meeting.meetingName || '');
            setDescription(meeting.description || '');
            setCategory(meeting.category || '');
            setMaxMembers(meeting.maxMembers || 10);
            setIsPublic(meeting.isPublic !== false);
            setIsRegularMeeting(meeting.isRegularMeeting || false);
            setRepeatType(meeting.repeatType || 'WEEKLY');
            setRepeatInterval(meeting.repeatInterval || 1);
            setLocation(meeting.placeName || '');

            // 여행 모임 정보 설정
            setMeetingType(meeting.meetingType || 'DAY');
            setTravelDestination(meeting.travelDestination || '');
            setTravelType(meeting.travelType || 'DOMESTIC');
            setTravelStartDate(meeting.travelStartDate ? new Date(meeting.travelStartDate) : null);
            setTravelEndDate(meeting.travelEndDate ? new Date(meeting.travelEndDate) : null);
            setMeetingTime(meeting.meetingTime || '');
            setHotelName(meeting.hotelName || '');
            setHotelAddress(meeting.hotelAddress || '');

            // schedule이 있으면 날짜/시간 설정
            if (meeting.schedule && meeting.schedule.startDatetime) {
                try {
                    console.log('=== MeetingEdit 시간 표시 디버깅 ===');
                    console.log('API에서 받은 startDatetime:', meeting.schedule.startDatetime);
                    const startDate = new Date(meeting.schedule.startDatetime);
                    console.log('Date 객체:', startDate.toString());
                    console.log('toLocaleString:', startDate.toLocaleString('ko-KR'));
                    if (!isNaN(startDate.getTime())) {
                        const formattedDate = startDate.toLocaleDateString('ko-KR', { year: 'numeric', month: 'long', day: 'numeric', weekday: 'short' });
                        const formattedTime = startDate.toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' });
                        console.log('포맷된 날짜:', formattedDate, '시간:', formattedTime);
                        setDate(formattedDate);
                        setTime(formattedTime);
                    }
                } catch (error) {
                    console.error('날짜/시간 파싱 오류:', error);
                }
            } else {
                console.log('Schedule 데이터가 없습니다.');
            }
        }
    }, [meetingData]);

    const isSaveEnabled = meetingName.trim().length > 0;

    // 여행 모임인지 확인
    const isTravelMeeting = meetingType === 'TRAVEL';

    // 여행 기간 계산
    const travelPeriod = useMemo(() => {
        if (!travelStartDate || !travelEndDate) return null;
        const diffTime = Math.abs(travelEndDate.getTime() - travelStartDate.getTime());
        const days = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
        const nights = days - 1;
        return { days, nights };
    }, [travelStartDate, travelEndDate]);

    const handleSave = async () => {
        if (isSaveEnabled && meetingId) {
            const updateData: any = {
                meetingName,
                description,
                category,
                maxMembers,
                isPublic,
                placeName: location,
            };

            if (isTravelMeeting) {
                // 여행 모임 필드
                updateData.meetingType = 'TRAVEL';
                updateData.travelDestination = travelDestination;
                updateData.travelType = travelType;
                updateData.travelStartDate = travelStartDate?.toISOString();
                updateData.travelEndDate = travelEndDate?.toISOString();
                updateData.meetingTime = meetingTime;
            } else {
                // 당일 모임 필드
                updateData.meetingType = 'DAY';
                updateData.isRegularMeeting = isRegularMeeting;
                updateData.repeatType = isRegularMeeting ? repeatType : null;
                updateData.repeatInterval = isRegularMeeting ? repeatInterval : null;
            }

            try {
                await meetingStore.onUpdateMeeting(meetingId, updateData);
                navigate(`/meeting/detail/${meetingId}`, { replace: true });
            } catch (error) {
                console.error('모임 수정 실패:', error);
                // TODO: 에러 알림 표시
            }
        }
    };

    const handleCancel = () => {
        navigate(`/meeting/detail/${meetingId}`);
    };

    const handleDateChange = () => {
        // 현재 모임 정보를 함께 전달
        const scheduleDate = meetingData?.data?.schedule?.startDatetime
            ? new Date(meetingData.data.schedule.startDatetime).toISOString()
            : null;

        navigate('/meeting/create/step2', {
            state: {
                selectedPlace: location,
                placeInfo: meetingData?.data ? {
                    name: meetingData.data.placeName,
                    address: meetingData.data.placeAddress,
                    lat: meetingData.data.latitude,
                    lng: meetingData.data.longitude
                } : null,
                selectedTime: time,  // 현재 시간 정보
                selectedDate: scheduleDate,  // 현재 날짜 정보
                isEditMode: true,
                meetingId: meetingId,
                confirmedScheduleId: meetingData?.data?.confirmedScheduleId,  // 기존 스케줄 ID
                // 모임 기본 정보 (API 업데이트에 필요)
                meetingName: meetingName,
                description: description,
                category: category,
                maxMembers: maxMembers,
                isPublic: isPublic
            }
        });
    };

    const handleLocationChange = () => {
        // 현재 모임 정보를 함께 전달
        const scheduleDate = meetingData?.data?.schedule?.startDatetime
            ? new Date(meetingData.data.schedule.startDatetime).toISOString()
            : null;

        navigate('/meeting/place-search', {
            state: {
                selectedPlace: location,
                placeInfo: meetingData?.data ? {
                    name: meetingData.data.placeName,
                    address: meetingData.data.placeAddress,
                    lat: meetingData.data.latitude,
                    lng: meetingData.data.longitude
                } : null,
                selectedTime: time,
                selectedDate: scheduleDate,
                isEditMode: true,
                meetingId: meetingId,
                confirmedScheduleId: meetingData?.data?.confirmedScheduleId,  // 기존 스케줄 ID
                // 모임 기본 정보 (API 업데이트에 필요)
                meetingName: meetingName,
                description: description,
                category: category,
                maxMembers: maxMembers,
                isPublic: isPublic
            }
        });
    };

    return (
        <div className="meeting-edit-wrapper">
            <Header title="모임 수정" isSubPage />

            <div className="meeting-edit-content">
                {/* Title Section */}
                <div className="edit-title-section">
                    <h1 className="edit-title">모임 정보를 수정하세요</h1>
                    <p className="edit-subtitle">수정된 내용은 모든 참여자에게 알림이 전송됩니다.</p>
                </div>

                {/* 모임 타입 표시 (수정 불가) */}
                <div className="meeting-type-indicator">
                    <span className={`meeting-type-badge-edit ${isTravelMeeting ? 'travel' : 'day'}`}>
                        {isTravelMeeting ? '✈️ 여행 모임' : '📅 당일 모임'}
                    </span>
                    {isTravelMeeting && travelPeriod && (
                        <span className="travel-period-info">
                            {travelPeriod.nights}박 {travelPeriod.days}일
                        </span>
                    )}
                </div>

                {/* Form Section */}
                <div className="edit-form">
                    {/* Meeting Name */}
                    <div className="form-group">
                        <label className="form-label">
                            모임명 <span className="required">*</span>
                        </label>
                        <input
                            type="text"
                            className="form-input"
                            placeholder="예) 맛집 탐방"
                            value={meetingName}
                            onChange={(e) => setMeetingName(e.target.value.slice(0, 20))}
                            maxLength={20}
                        />
                        <div className="form-hint">
                            <span className="char-count">{meetingName.length}/20</span>
                        </div>
                    </div>

                    {/* Description */}
                    <div className="form-group">
                        <label className="form-label">설명 (선택)</label>
                        <textarea
                            className="form-textarea"
                            placeholder="모임에 대한 간단한 설명을 적어주세요."
                            value={description}
                            onChange={(e) => setDescription(e.target.value.slice(0, 200))}
                            maxLength={200}
                            rows={4}
                        />
                        <div className="form-hint">
                            <span className="char-count">{description.length}/200</span>
                        </div>
                    </div>

                    {/* 여행 모임 전용 필드 */}
                    {isTravelMeeting ? (
                        <>
                            {/* 여행지 */}
                            <div className="form-group">
                                <label className="form-label">여행지</label>
                                <input
                                    type="text"
                                    className="form-input"
                                    placeholder="예) 제주도, 부산"
                                    value={travelDestination}
                                    onChange={(e) => setTravelDestination(e.target.value)}
                                />
                            </div>

                            {/* 여행 타입 */}
                            <div className="form-group">
                                <label className="form-label">여행 타입</label>
                                <div className="travel-type-buttons">
                                    <button
                                        className={`travel-type-button ${travelType === 'DOMESTIC' ? 'selected' : ''}`}
                                        onClick={() => setTravelType('DOMESTIC')}
                                    >
                                        🇰🇷 국내
                                    </button>
                                    <button
                                        className={`travel-type-button ${travelType === 'OVERSEAS' ? 'selected' : ''}`}
                                        onClick={() => setTravelType('OVERSEAS')}
                                    >
                                        🌏 해외
                                    </button>
                                </div>
                            </div>

                            {/* 여행 기간 */}
                            <div className="form-group">
                                <label className="form-label">여행 기간</label>
                                <div className="date-range-inputs">
                                    <div className="date-input-wrapper">
                                        <label className="date-input-label">시작일</label>
                                        <input
                                            type="date"
                                            className="form-input date-input"
                                            value={travelStartDate ? travelStartDate.toISOString().split('T')[0] : ''}
                                            onChange={(e) => setTravelStartDate(e.target.value ? new Date(e.target.value) : null)}
                                        />
                                    </div>
                                    <div className="date-range-separator">~</div>
                                    <div className="date-input-wrapper">
                                        <label className="date-input-label">종료일</label>
                                        <input
                                            type="date"
                                            className="form-input date-input"
                                            value={travelEndDate ? travelEndDate.toISOString().split('T')[0] : ''}
                                            onChange={(e) => setTravelEndDate(e.target.value ? new Date(e.target.value) : null)}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* 집결 시간 */}
                            <div className="form-group">
                                <label className="form-label">집결 시간</label>
                                <input
                                    type="time"
                                    className="form-input time-input"
                                    value={meetingTime}
                                    onChange={(e) => setMeetingTime(e.target.value)}
                                />
                            </div>

                            {/* 집결 장소 */}
                            <div className="form-group">
                                <label className="form-label">집결 장소</label>
                                <div className="info-display-item" onClick={handleLocationChange}>
                                    <div className="info-display-content">
                                        <div className="info-display-icon">📍</div>
                                        <div className="info-display-text">
                                            <div className="info-display-label">위치</div>
                                            <div className="info-display-value">{location || '장소 미정'}</div>
                                        </div>
                                    </div>
                                    <div className="info-display-arrow">→</div>
                                </div>
                            </div>

                            {/* 숙소 정보 */}
                            <div className="form-group">
                                <label className="form-label">숙소 정보 (선택)</label>
                                <input
                                    type="text"
                                    className="form-input"
                                    placeholder="숙소명"
                                    value={hotelName}
                                    onChange={(e) => setHotelName(e.target.value)}
                                    style={{ marginBottom: '8px' }}
                                />
                                <input
                                    type="text"
                                    className="form-input"
                                    placeholder="숙소 주소"
                                    value={hotelAddress}
                                    onChange={(e) => setHotelAddress(e.target.value)}
                                />
                            </div>
                        </>
                    ) : (
                        <>
                            {/* 당일 모임 - Date and Time */}
                            <div className="form-group">
                                <label className="form-label">날짜 및 시간</label>
                                <div className="info-display-item" onClick={handleDateChange}>
                                    <div className="info-display-content">
                                        <div className="info-display-icon">📅</div>
                                        <div className="info-display-text">
                                            <div className="info-display-label">날짜</div>
                                            <div className="info-display-value">{date || '날짜 미정'}</div>
                                        </div>
                                    </div>
                                    <div className="info-display-arrow">→</div>
                                </div>
                                <div className="info-display-item" onClick={handleDateChange}>
                                    <div className="info-display-content">
                                        <div className="info-display-icon">⏰</div>
                                        <div className="info-display-text">
                                            <div className="info-display-label">시간</div>
                                            <div className="info-display-value">{time || '시간 미정'}</div>
                                        </div>
                                    </div>
                                    <div className="info-display-arrow">→</div>
                                </div>
                            </div>

                            {/* Location */}
                            <div className="form-group">
                                <label className="form-label">장소</label>
                                <div className="info-display-item" onClick={handleLocationChange}>
                                    <div className="info-display-content">
                                        <div className="info-display-icon">📍</div>
                                        <div className="info-display-text">
                                            <div className="info-display-label">위치</div>
                                            <div className="info-display-value">{location || '장소 미정'}</div>
                                        </div>
                                    </div>
                                    <div className="info-display-arrow">→</div>
                                </div>
                            </div>

                            {/* Regular Meeting Toggle */}
                            <div className="form-group">
                                <div className="toggle-container">
                                    <div className="toggle-info">
                                        <span className="toggle-label">정기 모임 설정</span>
                                        <span className="toggle-description">매주/매월 반복되는 모임인가요?</span>
                                    </div>
                                    <button
                                        className={`toggle-switch ${isRegularMeeting ? 'active' : ''}`}
                                        onClick={() => setIsRegularMeeting(!isRegularMeeting)}
                                    >
                                        <div className="toggle-knob"></div>
                                    </button>
                                </div>

                                {/* Repeat Options - shown when toggle is ON */}
                                {isRegularMeeting && (
                                    <div className="repeat-options">
                                        <label className="form-label">반복 주기</label>
                                        <div className="repeat-buttons">
                                            <button
                                                className={`repeat-button ${repeatType === 'WEEKLY' && repeatInterval === 1 ? 'selected' : ''}`}
                                                onClick={() => { setRepeatType('WEEKLY'); setRepeatInterval(1); }}
                                            >
                                                매주
                                            </button>
                                            <button
                                                className={`repeat-button ${repeatType === 'WEEKLY' && repeatInterval === 2 ? 'selected' : ''}`}
                                                onClick={() => { setRepeatType('WEEKLY'); setRepeatInterval(2); }}
                                            >
                                                격주
                                            </button>
                                            <button
                                                className={`repeat-button ${repeatType === 'MONTHLY' ? 'selected' : ''}`}
                                                onClick={() => { setRepeatType('MONTHLY'); setRepeatInterval(1); }}
                                            >
                                                매월
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </div>

                {/* Warning Message */}
                <div className="edit-warning">
                    <div className="warning-icon">⚠️</div>
                    <div className="warning-text">
                        모임 정보를 수정하면 모든 참여자에게 알림이 전송됩니다.
                    </div>
                </div>
            </div>

            {/* Action Buttons */}
            <div className="meeting-edit-footer">
                <div className="edit-buttons">
                    <button className="cancel-button" onClick={handleCancel}>
                        취소
                    </button>
                    <Button
                        text="저장"
                        disabled={!isSaveEnabled}
                        onPress={handleSave}
                    />
                </div>
            </div>
        </div>
    );
};
